import { axios } from '@/plugins/axios';
import merge from 'lodash/merge';
import { Resource, Pagination, Statement } from '@/helpers/Interfaces';
import Pager from '@/helpers/Pager';
import { DateTime } from 'luxon';

/**
 * shopsService
 */
export default class ShopsService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`admin/store/shops`, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/store/shops/${id}`)).data;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async create(model: FormModel):  Promise<ResponseModel>
    {
        return (await axios.post(`admin/store/shops`, model)).data as ResponseModel;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel):  Promise<ResponseModel>
    {
        return (await axios.put(`admin/store/shops/${id}`, model)).data as ResponseModel;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/store/shops/${id}`)).data as Statement;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<AutocompleteListModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 10, hasPriceList: boolean = null): Promise<AutocompleteListModel[]>
    {
        return (await axios.get<AutocompleteListModel[]>('admin/store/shops/autocomplete', {
            params: {
                search: search,
                limit: limit,
                hasPriceList: hasPriceList
            }
        })).data;
    }

    /**
    * @param id number
    *
    * @returns Promise<AutocompleteListModel>
    */
    public static async fetchAutocompleteModel(id: number): Promise<AutocompleteListModel[]>
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/shops/autocomplete/${id}`)).data;
    }

    /**
     * @param countryId number
     *
     * @returns Promise<TaxationsListModel[]>
     */
    public static async getTaxationsListByCountryId(countryId: number): Promise<TaxationsListModel[]>
    {
        return (await axios.get<TaxationsListModel[]>(`admin/store/shops/taxations/${countryId}`)).data;
    }

    /**
    * @param id countryId
    *
    * @returns Promise<CarriersModel>
    */
    public static async getCarriersWithPayments(countryId: number): Promise<CarriersModel[]>
    {
        return (await axios.get<CarriersModel[]>(`admin/store/shops/carriers/${countryId}`)).data;
    }

    /**
    * @param id countryId
    *
    * @returns Promise<CarriersModel>
    */
    public static async getWebsitesList(shopId: number): Promise<WebsiteListModel[]>
    {
        return (await axios.get<WebsiteListModel[]>(`admin/store/shops/websites/${shopId}`)).data;
    }
}

export enum CatalogCategoryTitlesEnum {
    Hide = 'Hide',
    ShowToLastLevel = 'ShowLastLevel',
    ShowToSelectedLevel = 'ShowToSelectedLevel'
}

export interface ListItemModel
{
    id: number;
    name: string;
    website: string;
    dateModifiedUtc?: DateTime;
}

export interface FormModel
{
    id: number;
    name: string;
    description: string;
    priceFormat: string;
    countryId: number;
    currencyId: number;
    timeZone: string;
    categoryFolders: number[];

    carriers: number[];
    websites: Record<number, string[]>;
    labels: number[];

    orderByGuest: boolean;
    orderOutOfStock: boolean;
    historyExpireTime: number;
    historyMaxItems: number;
    quantityReservationTimeout: number;
    returnTimeout: number;
    reclamationTimeout: number;
    catalogCategoryTitles: CatalogCategoryTitlesEnum;
    catalogCategoryTitlesLevel: number;

}
export interface FilterModel
{
    name: string;
    createdBy?: number;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    countryId?: number;
    currencyId?: number;
}

export interface AutocompleteListModel
{
    id: number;
    name: string;
    isActive: boolean;
}

export interface CurrentShop
{
    shop: number;
}

export interface ResponseModel
{
    id: number;
}

export interface FormCarriersModel
{
    isActive: boolean;
    payments: Record<number, boolean>;
}

export interface CarriersModel
{
    carrier: AutocompleteListModel;
    payments: AutocompleteListModel[];
}

export interface TaxationsListModel
{
    id: number;
    name: string;
    value: number;
}

export interface WebsiteListModel
{
    id: number;
    name: string;
    locales: LocaleModel[];
}

export interface LocaleModel
{
    disabled: boolean;
    name: string;
}
